import { z } from 'zod';
import { http, ApiError } from '@/services/http';
import { env } from '@/lib/env';

// ══════════════════════════════════════════════════════════════════════════
// ÂMBITO DE NETWORKING — o contrato explícito (espelha api/src/domain/networking-scope.js).
//
// O `eventId?: string` opcional era ambíguo: a ausência queria dizer "rede
// global", "esqueci-me" e "cliente antigo" ao mesmo tempo, e cada camada
// adivinhava. Agora o âmbito é sempre dito, e diz UMA coisa:
//
//   GLOBAL → a rede permanente da pessoa. O perfil e as ligações vivem aqui.
//   EVENT  → o contexto onde se DESCOBRE gente (bilhete verificado no servidor).
//
// Uma ligação criada num evento fica global; o evento fica como proveniência.
// COMMUNITY/ORGANIZATION entram nesta união quando existirem no servidor.
// ══════════════════════════════════════════════════════════════════════════
export type NetworkingScope =
  | { type: 'GLOBAL' }
  | { type: 'EVENT'; id: string };

export const GLOBAL_SCOPE: NetworkingScope = { type: 'GLOBAL' };
export const eventScope = (id: string): NetworkingScope => ({ type: 'EVENT', id });
/** Evento ativo → contexto; sem evento → a rede permanente. Sem perguntar nada ao utilizador. */
export const scopeFor = (activeEventId?: string | null): NetworkingScope =>
  (activeEventId ? eventScope(activeEventId) : GLOBAL_SCOPE);

/** Forma compacta aceite pela API: `global` | `event:<uuid>`. */
export const scopeParam = (s: NetworkingScope): string =>
  (s.type === 'GLOBAL' ? 'global' : `event:${s.id}`);
const scopeQuery = (s: NetworkingScope, sep = '?') => `${sep}scope=${encodeURIComponent(scopeParam(s))}`;

/** A razão que o servidor dá quando um âmbito não autoriza a ligação. */
export function scopeErrorMessage(err: unknown): string | null {
  const body = err instanceof ApiError ? (err.body as { error?: string; message?: string } | undefined) : undefined;
  if (!body?.error) return null;
  return body.message || null;
}

// ── Tap / Networking — portado da raiz para a stack lean. ──
// Endpoints reais (api-client.js): /qr/generate, /users/by-qr, /users/:id,
// /users/:id/compatibility, /users/:id/taps, /users/:id/connections,
// /users/me/connect, /connections/:id/respond, /users/me/recommendations, /nfc/tap.

// ── O meu QR (imagem data-url + tap_url + qr_code_id público imutável). ──
const myQrSchema = z.object({
  data: z.string().nullish(),        // data-url da imagem PNG
  tap_url: z.string().nullish(),     // URL que o QR/NFC codificam
  qr_code_id: z.string().nullish(),  // id público imutável
}).passthrough();
export type MyQr = z.infer<typeof myQrSchema>;

export async function getMyQr(userId: string): Promise<MyQr> {
  const raw = await http.get<unknown>(`/qr/generate/${userId}?format=data-url&size=320`);
  return myQrSchema.parse(raw ?? {});
}

// ── Perfil público de uma pessoa (por qr_code_id OU uuid). ──
const personSchema = z.object({
  id: z.string().nullish(),
  qr_code_id: z.string().nullish(),
  full_name: z.string().nullish(),
  first_name: z.string().nullish(),
  job_title: z.string().nullish(),
  company: z.string().nullish(),
  sector: z.string().nullish(),
  bio: z.string().nullish(),
  avatar_url: z.string().nullish(),
  match_score: z.coerce.number().nullish(),
  // ── Cartão de visita permanente (público) ──
  email: z.string().nullish(),
  phone: z.string().nullish(),
  location: z.string().nullish(),
  skills: z.string().nullish(),
  whatsapp: z.string().nullish(),
  website: z.string().nullish(),
  linkedin: z.string().nullish(),
  twitter: z.string().nullish(),
  instagram: z.string().nullish(),
  facebook: z.string().nullish(),
  github: z.string().nullish(),
  behance: z.string().nullish(),
  portfolio_url: z.string().nullish(),
  cv_url: z.string().nullish(),
  pitch_url: z.string().nullish(),
}).passthrough();
export type Person = z.infer<typeof personSchema>;

/** Tenta por qr_code_id; cai para /users/:id (mesma lógica do api-client legacy). */
export async function getPublicProfile(idOrQr: string): Promise<Person | null> {
  const enc = encodeURIComponent(idOrQr);
  const unwrap = (r: unknown) => (r && (r as { data?: unknown }).data) ?? r;
  try {
    return personSchema.parse(unwrap(await http.get<unknown>(`/users/by-qr/${enc}`)));
  } catch {
    try {
      return personSchema.parse(unwrap(await http.get<unknown>(`/users/${enc}`)));
    } catch {
      return null;
    }
  }
}

// ── Compatibilidade (motor SQL SSOT) com uma pessoa. ──
const compatSchema = z.object({
  score: z.coerce.number().nullish(),
  match_score: z.coerce.number().nullish(),
  reasons: z.array(z.string()).nullish(),
  shared: z.array(z.string()).nullish(),
}).passthrough();
export type Compatibility = z.infer<typeof compatSchema>;

export async function getCompatibility(targetId: string): Promise<Compatibility | null> {
  try {
    return compatSchema.parse(await http.get<unknown>(`/users/${encodeURIComponent(targetId)}/compatibility`));
  } catch {
    return null;
  }
}

// ── Ligações / taps recentes. ──
const connSchema = z.object({
  id: z.string().nullish(),
  partner_id: z.string().nullish(),
  user_b_id: z.string().nullish(),
  partner_name: z.string().nullish(),
  full_name: z.string().nullish(),
  partner_title: z.string().nullish(),
  job_title: z.string().nullish(),
  partner_company: z.string().nullish(),
  company: z.string().nullish(),
  qr_code_id: z.string().nullish(),
  status: z.string().nullish(),
  match_score: z.coerce.number().nullish(),
}).passthrough();
export type Connection = z.infer<typeof connSchema>;

function toArray(raw: unknown): unknown[] {
  if (Array.isArray(raw)) return raw;
  const d = (raw as { data?: unknown })?.data;
  return Array.isArray(d) ? d : [];
}

/**
 * A minha rede. Sem âmbito → GLOBAL: o grafo inteiro, porque uma ligação
 * sobrevive ao evento onde nasceu. Com `eventScope(id)` → filtro por
 * proveniência ("quem conheci neste evento"), que é o que a vista do Tap quer.
 */
export async function getConnections(userId: string, scope: NetworkingScope = GLOBAL_SCOPE): Promise<Connection[]> {
  const raw = await http.get<unknown>(`/users/${userId}/connections${scopeQuery(scope)}`);
  return z.array(connSchema).parse(toArray(raw));
}

export async function getUserTaps(userId: string, limit = 25): Promise<Connection[]> {
  const raw = await http.get<unknown>(`/users/${userId}/taps?limit=${limit}`);
  return z.array(connSchema).parse(toArray(raw));
}

/**
 * Descoberta — CONTEXTUAL. Dentro de um evento só se recomenda quem lá está (o
 * servidor verifica bilhetes): antes recomendava-se gente da base inteira a quem
 * não te podias ligar. Fora de um evento, a rede global continua a sugerir.
 */
export async function getRecommendations(scope: NetworkingScope = GLOBAL_SCOPE): Promise<Person[]> {
  try {
    const raw = await http.get<unknown>(`/users/me/recommendations?limit=10${scopeQuery(scope, '&')}`);
    return z.array(personSchema).parse(toArray(raw));
  } catch {
    return [];
  }
}

// ── Pedir ligação / responder. ──
// O âmbito vai SEMPRE explícito e o servidor decide o que ele autoriza:
//   • EVENT  → liga-se a quem está no evento (o bilhete é verificado lá; 403 se forjado).
//   • GLOBAL → liga-se a quem já se cruzou (tap ou evento em comum). Sem essa
//              história partilhada vem 403 `shared_context_required` — com a
//              razão em texto, que o ProfilePage mostra em vez de inventar uma.
// Não cabe ao cliente antecipar a decisão: cabe-lhe dizer onde está e mostrar
// a resposta.
export async function connectRequest(
  targetUserId: string, scope: NetworkingScope = GLOBAL_SCOPE, kind: 'match' | 'intro' | 'follow_up' = 'match',
) {
  return http.post<{ ok?: boolean; status?: string; connection_id?: string }>('/users/me/connect', {
    target_user_id: targetUserId, scope: scopeParam(scope), kind,
  });
}
export async function respondConnection(connectionId: string, accept: boolean) {
  return http.post<{ ok?: boolean }>(`/connections/${connectionId}/respond`, { accept });
}

// ── Pedidos pendentes (consentimento mútuo, migração 075) ──
const pendingSchema = z.object({
  id: z.string(),
  user_a_id: z.string().nullish(),   // quem pediu
  user_b_id: z.string().nullish(),   // quem decide
  user_a_name: z.string().nullish(),
  user_b_name: z.string().nullish(),
  user_a_avatar: z.string().nullish(),
  match_score: z.coerce.number().nullish(),
  created_at: z.string().nullish(),
}).passthrough();
export type PendingConnection = z.infer<typeof pendingSchema>;

export async function getPendingConnections(): Promise<PendingConnection[]> {
  const raw = await http.get<unknown>('/connections/pending');
  return z.array(pendingSchema).parse(toArray(raw));
}

// ══════════════════════════════════════════════════════════════════════════
// TAP — a escrita mais importante da app (cada tap é uma aresta do grafo).
//
// Dois bugs viviam aqui:
//   1. O payload não era o contrato do endpoint. POST /nfc/tap exige
//      user_id + tag_id + tap_duration_ms; enviava-se {qr_code_id, source,
//      event_id} → 400 em TODOS os taps.
//   2. O erro era engolido (`.catch(() => {})`) → o 400 acima era invisível, e
//      um tap sem rede desaparecia em silêncio.
//
// Agora: tapar = ENFILEIRAR (durável, sobrevive a fechar a app). O envio real
// acontece no outbox, que reenvia até o servidor confirmar. O UUID da operação
// é o client_tap_id → o servidor deduplica (migração 069).
// ══════════════════════════════════════════════════════════════════════════

/** O que guardamos no momento do tap: só o que temos à mão, sem rede. */
export interface QueuedTap {
  /** qr_code_id ou uuid lido da tag/QR. */
  target: string;
  eventId?: string;
  /** Duração do toque na tag (Web NFC). O scan por câmara/link é instantâneo. */
  durationMs: number;
}

/** Regista um tap. Resolve assim que está em disco — NÃO espera pela rede. */
export async function queueTap(tap: QueuedTap): Promise<void> {
  const { enqueue } = await import('@/lib/outbox');
  await enqueue<QueuedTap>('tap', tap);
}

/** Envio real (chamado pelo outbox, já com rede). O opId é o client_tap_id. */
export async function sendTap(tap: QueuedTap, opId: string): Promise<unknown> {
  const { getMe } = await import('@/services/me');
  const me = await getMe();

  // O QR codifica o qr_code_id público; a tabela guarda o UUID do alvo. A
  // resolução faz-se aqui, no envio (temos rede por definição), e não no tap.
  const target = await getPublicProfile(tap.target);

  return http.post<{ tap_id?: string; deduplicated?: boolean }>('/nfc/tap', {
    user_id: me.id,                       // o backend recusa taps em nome de terceiros
    tag_id: tap.target,                   // o que a tag/QR continha → card_uid
    target_uid: target?.id ?? undefined,
    event_id: tap.eventId,
    tap_duration_ms: tap.durationMs,
    client_tap_id: opId,                  // chave de idempotência (069)
  });
}

// ── URL de partilha rica (passa pelo endpoint OG). ──
export function shareUrlFor(qrCodeId: string): string {
  return `${env.apiOrigin}/p/${qrCodeId}`;
}
